import type { ChunkRange } from "./view-chunks.ts";

/**
 * Loading the chunks a camera needs a little at a time (ADR-019's amendment of 2026-10-03): a stream keeps, for each
 * chunk it has touched, how many of the chunk's units of work are done, and each step moves the chunks toward the
 * range wanted. A unit is whatever the caller makes it (painting one tile layer, placing one slice of a chunk's
 * props); the stream only counts them. The chunks the urgent range holds, the ones on screen, are finished in the step
 * that finds them unfinished, whatever that costs, so nothing on screen is ever missing; every other chunk is worked
 * on until the step's time budget is spent, at least one unit a step, so the one-chunk margin round the view loads
 * over the frames it stays off screen. Chunks the range left are unloaded first, last unit first, so what they free
 * can be reused by the chunks coming in. A step with nothing to do reads no clock, calls nothing and allocates
 * nothing.
 *
 * Plain data and functions: the stream is a record the caller keeps, and the same calls give the same result.
 */

/**
 * One chunk in a stream: its position, how many of its units are done, how many it has, and whether the range wants
 * it. A wanted chunk loads toward `total`; one not wanted unloads toward 0 and is then forgotten.
 */
export interface ChunkStreamEntry {
  readonly cx: number;
  readonly cy: number;
  readonly total: number;
  done: number;
  wanted: boolean;
}

/**
 * A stream: its chunks, the range last wanted, and the units still to apply or revert.
 */
export interface ChunkStream {
  readonly entries: ChunkStreamEntry[];
  readonly target: { minCx: number; minCy: number; maxCx: number; maxCy: number };
  targeted: boolean;
  pending: number;
}

/**
 * The work of loading chunks, which the caller supplies.
 */
export interface ChunkWork {
  /**
   * How many units a chunk has, asked once as the chunk enters the stream.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @returns A whole number of at least 0.
   */
  units(cx: number, cy: number): number;

  /**
   * Does one unit of a chunk. Units are applied in order from 0. A unit that throws is not counted and is tried again
   * by the next step, so applying a unit twice must do no harm. While a unit of a chunk off screen keeps throwing,
   * the step stops there every frame: the chunks nearer the urgent range, picked before it, are loaded, and those
   * picked after it are not, though each still loads at once when the urgent range comes to hold it.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @param unit The unit's index.
   */
  apply(cx: number, cy: number, unit: number): void;

  /**
   * Undoes one unit of a chunk. Units are reverted in order from the last applied. Unloading comes before loading,
   * so a revert that keeps throwing stops every step there: nothing off screen loads until the chunk is wanted again,
   * and only the urgent range's chunks are still finished. A revert should not throw.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @param unit The unit's index.
   */
  revert(cx: number, cy: number, unit: number): void;
}

/**
 * How long a step may work on chunks off screen: the milliseconds, and the clock they are read from.
 */
export interface ChunkBudget {
  readonly ms: number;
  readonly now: () => number;
}

/**
 * An empty stream.
 *
 * @returns The stream.
 */
export function createChunkStream(): ChunkStream {
  return { entries: [], target: { minCx: 0, minCy: 0, maxCx: -1, maxCy: -1 }, targeted: false, pending: 0 };
}

/**
 * Whether a range holds a chunk.
 *
 * @param range The range.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns True when it does.
 */
function holds(range: ChunkRange, cx: number, cy: number): boolean {
  return cx >= range.minCx && cx <= range.maxCx && cy >= range.minCy && cy <= range.maxCy;
}

/**
 * How many chunks lie between a chunk and a range, along the longer axis: 0 inside it.
 *
 * @param range The range.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns The distance in chunks.
 */
function distance(range: ChunkRange, cx: number, cy: number): number {
  const dx = cx < range.minCx ? range.minCx - cx : cx > range.maxCx ? cx - range.maxCx : 0;
  const dy = cy < range.minCy ? range.minCy - cy : cy > range.maxCy ? cy - range.maxCy : 0;
  return dx > dy ? dx : dy;
}

/**
 * Points a stream at a new range: every chunk it holds is marked wanted or not, chunks of the range it lacks enter
 * with their unit count, chunks not wanted with nothing done are forgotten, and the pending count is taken again.
 *
 * @param stream The stream.
 * @param target The range wanted.
 * @param work The caller's work, asked each entering chunk's units.
 * @throws RangeError when a chunk's unit count is not a whole number of at least 0.
 */
function retarget(stream: ChunkStream, target: ChunkRange, work: ChunkWork): void {
  const entries = stream.entries;
  for (let cy = target.minCy; cy <= target.maxCy; cy += 1) {
    for (let cx = target.minCx; cx <= target.maxCx; cx += 1) {
      let known = false;
      for (let index = 0; index < entries.length && !known; index += 1) {
        const entry = entries[index] as ChunkStreamEntry;
        known = entry.cx === cx && entry.cy === cy;
      }
      if (!known) {
        const total = work.units(cx, cy);
        if (!Number.isInteger(total) || total < 0) {
          throw new RangeError(`chunk ${cx},${cy} has ${total} units; expected a whole number of at least 0`);
        }
        entries.push({ cx, cy, total, done: 0, wanted: true });
      }
    }
  }
  let kept = 0;
  let pending = 0;
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index] as ChunkStreamEntry;
    entry.wanted = holds(target, entry.cx, entry.cy);
    if (!entry.wanted && entry.done === 0) {
      continue;
    }
    pending += entry.wanted ? entry.total - entry.done : entry.done;
    entries[kept] = entry;
    kept += 1;
  }
  entries.length = kept;
  stream.pending = pending;
  stream.target.minCx = target.minCx;
  stream.target.minCy = target.minCy;
  stream.target.maxCx = target.maxCx;
  stream.target.maxCy = target.maxCy;
  stream.targeted = true;
}

/**
 * The index of the chunk a step works on next: the first one unloading, else the unfinished wanted chunk nearest the
 * near range, the earliest of equals.
 *
 * @param stream The stream.
 * @param near The range to load nearest first: the urgent range, or the target without one.
 * @returns The index into the stream's entries, or -1 when nothing is left to do.
 */
function nextIndex(stream: ChunkStream, near: ChunkRange): number {
  const entries = stream.entries;
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index] as ChunkStreamEntry;
    if (!entry.wanted) {
      if (entry.done > 0) {
        return index;
      }
    } else if (entry.done < entry.total) {
      const away = distance(near, entry.cx, entry.cy);
      if (away < bestDistance) {
        best = index;
        bestDistance = away;
      }
    }
  }
  return best;
}

/**
 * Moves a stream one step toward a range: finishes every wanted chunk the urgent range holds, then unloads and loads
 * the rest until the budget's time is spent, at least one unit. While the range is the one last wanted and nothing is
 * pending, the step does nothing at all.
 *
 * @param stream The stream, changed in place.
 * @param target The chunk range wanted loaded.
 * @param urgent The chunks that must be whole when the step returns (those on screen), or null for none.
 * @param work The caller's work.
 * @param budget The time the step may spend on chunks the urgent range does not hold.
 * @returns How many units the step applied or reverted.
 * @throws RangeError when a chunk's unit count is not a whole number of at least 0, and whatever a unit throws: that
 *   unit is not counted, and the next step tries it again, so a unit off screen that keeps throwing holds up the
 *   units behind it (`ChunkWork.apply`, `ChunkWork.revert`). The urgent pass has run by then.
 * @example
 * const stream = createChunkStream();
 * stepChunkStream(stream, visibleChunks(view, 32, 32, columns, rows), onScreen, work, { ms: 3, now: () => performance.now() });
 */
export function stepChunkStream(stream: ChunkStream, target: ChunkRange, urgent: ChunkRange | null, work: ChunkWork, budget: ChunkBudget): number {
  const held = stream.target;
  if (!stream.targeted || held.minCx !== target.minCx || held.minCy !== target.minCy || held.maxCx !== target.maxCx || held.maxCy !== target.maxCy) {
    retarget(stream, target, work);
  }
  if (stream.pending === 0) {
    return 0;
  }
  const entries = stream.entries;
  let did = 0;
  if (urgent !== null) {
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index] as ChunkStreamEntry;
      if (!entry.wanted || !holds(urgent, entry.cx, entry.cy)) {
        continue;
      }
      while (entry.done < entry.total) {
        work.apply(entry.cx, entry.cy, entry.done);
        entry.done += 1;
        stream.pending -= 1;
        did += 1;
      }
    }
    if (stream.pending === 0) {
      return did;
    }
  }
  const near = urgent ?? held;
  const started = budget.now();
  do {
    const index = nextIndex(stream, near);
    if (index < 0) {
      break;
    }
    const entry = entries[index] as ChunkStreamEntry;
    if (entry.wanted) {
      work.apply(entry.cx, entry.cy, entry.done);
      entry.done += 1;
    } else {
      work.revert(entry.cx, entry.cy, entry.done - 1);
      entry.done -= 1;
      if (entry.done === 0) {
        entries.splice(index, 1);
      }
    }
    stream.pending -= 1;
    did += 1;
  } while (stream.pending > 0 && budget.now() - started < budget.ms);
  return did;
}

/**
 * Forgets a chunk the stream holds with nothing done, so the next step asks its units again: for a chunk that entered
 * before its data arrived, which entered with none (ADR-147 decision 8, a neighbouring map's chunk fetched as the view
 * nears it). A chunk with a unit applied is kept, since forgetting it would leave its units in place with nothing to
 * revert them.
 * @param stream The stream, changed in place.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns True when the stream no longer holds the chunk, false when it is kept for its applied units.
 */
export function forgetChunk(stream: ChunkStream, cx: number, cy: number): boolean {
  const entries = stream.entries;
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index] as ChunkStreamEntry;
    if (entry.cx !== cx || entry.cy !== cy) {
      continue;
    }
    if (entry.done > 0) {
      return false;
    }
    if (entry.wanted) {
      stream.pending -= entry.total;
    }
    entries.splice(index, 1);
    stream.targeted = false;
    return true;
  }
  return true;
}
